import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Button, Card, Col, Form, Row, Table, Spinner, Badge, Modal,
} from "react-bootstrap";
import {
  FaPlus, FaCheck, FaTimes, FaMoneyBillWave, FaEdit, FaUndo, FaLink,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import Select from "react-select";
import { buildChips, countActiveChips, resolveMapLabel } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import "./accountingFilters.css";
import "./accountingResponsive.css";
import { formatVND as fmtVND, toMoneyNumber } from "./utils/moneyFormat";
import MoneyInput from "./components/MoneyInput";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { Pagination } from "../../components/ui";
import { fetchAccountingPermissions, ACCOUNTING_SO_CHI_PREPAID } from "../../libs/customerCreatePermission";

const PAGE_SIZE = 20;

const STATUS_MAP = {
  DRAFT: { label: "Nháp", bg: "secondary" },
  APPROVED: { label: "Đã duyệt", bg: "info" },
  CONFIRMED: { label: "Đã xác nhận", bg: "success" },
  CANCELLED: { label: "Đã hủy", bg: "danger" },
};

const cardStyle = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};

const todayStr = () => new Date().toISOString().slice(0, 10);

const INITIAL_FORM = {
  name: "",
  expenseObjectId: "",
  amount: "",
  depreciationMonths: "12",
  expenseDate: todayStr(),
  bankAccountId: "",
  description: "",
};

const EMPTY_FILTERS = { status: "", keyword: "" };

const PrepaidExpenseList = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [expenseObjects, setExpenseObjects] = useState([]);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterKeyword, setFilterKeyword] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [perm, setPerm] = useState({ canCreate: false, canEdit: false, canDelete: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_SO_CHI_PREPAID);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const chipDefs = useMemo(() => [
    {
      key: "status",
      label: "Trạng thái",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveMapLabel(v, STATUS_MAP),
    },
    {
      key: "keyword",
      label: "Từ khóa",
      isActive: (v) => Boolean(v),
      formatValue: (v) => v,
    },
  ], []);

  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );

  const expenseObjectOptions = useMemo(() => expenseObjects.map((o) => ({
    value: String(o.id),
    label: `${o.code || o.id} — ${o.name || ""}`,
    object: o,
  })), [expenseObjects]);

  const selectedExpenseObject = useMemo(
    () => expenseObjectOptions.find((o) => o.value === String(form.expenseObjectId)) || null,
    [expenseObjectOptions, form.expenseObjectId]
  );

  const inheritedLines = useMemo(() => {
    const obj = selectedExpenseObject?.object;
    return Array.isArray(obj?.lineNames) ? obj.lineNames : [];
  }, [selectedExpenseObject]);

  useEffect(() => {
    axiosInstance.get("/api/accounting/bank-accounts/active")
      .then((res) => {
        const body = unwrap(res);
        const list = Array.isArray(body) ? body : (body.content || []);
        setBankAccounts(list);
      })
      .catch(() => setBankAccounts([]));
    axiosInstance.get("/api/accounting/expense-objects/active")
      .then((res) => {
        const body = unwrap(res);
        setExpenseObjects(Array.isArray(body) ? body : []);
      })
      .catch(() => setExpenseObjects([]));
  }, []);

  const fetchData = useCallback(async (p = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = { page: p, size: PAGE_SIZE };
      if (filters.status) params.status = filters.status;
      if (filters.keyword) params.keyword = filters.keyword;

      const res = await axiosInstance.get("/api/accounting/prepaid-expenses", { params });
      const body = unwrap(res);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setPage(p);
    } catch {
      toast.error("Không thể tải dữ liệu chi phí trả trước");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchData(0, EMPTY_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (next) => {
    setAppliedFilters(next);
    fetchData(0, next);
  };

  const handleSearch = () => applyFilters({
    status: filterStatus,
    keyword: filterKeyword.trim(),
  });

  const handleReset = () => {
    setFilterStatus("");
    setFilterKeyword("");
    applyFilters(EMPTY_FILTERS);
  };

  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters, [key]: "" };
    if (key === "status") setFilterStatus("");
    if (key === "keyword") setFilterKeyword("");
    applyFilters(base);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...INITIAL_FORM,
      expenseDate: todayStr(),
      bankAccountId: bankAccounts[0] ? String(bankAccounts[0].id) : "",
    });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      expenseObjectId: row.expenseObjectId ? String(row.expenseObjectId) : "",
      amount: row.amount != null ? String(row.amount) : "",
      depreciationMonths: row.depreciationMonths != null ? String(row.depreciationMonths) : "12",
      expenseDate: row.expenseDate || todayStr(),
      bankAccountId: row.bankAccountId ? String(row.bankAccountId) : "",
      description: row.description || "",
    });
    setShowModal(true);
  };

  const onFormChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.amount || !form.depreciationMonths) {
      toast.warn("Vui lòng điền tên, số tiền và thời gian khấu hao");
      return;
    }
    if (!form.expenseObjectId) {
      toast.warn("Vui lòng chọn đối tượng chi");
      return;
    }
    if (Number(form.depreciationMonths) < 1) {
      toast.warn("Thời gian khấu hao phải từ 1 tháng trở lên");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        expenseObjectId: Number(form.expenseObjectId),
        amount: toMoneyNumber(form.amount),
        depreciationMonths: Number(form.depreciationMonths),
        expenseDate: form.expenseDate || null,
        bankAccountId: form.bankAccountId ? Number(form.bankAccountId) : null,
        description: form.description || null,
      };
      if (editingId) {
        const res = await axiosInstance.put(`/api/accounting/prepaid-expenses/${editingId}`, payload);
        if (res?.data?.success === false) {
          toast.error(res.data.message || "Cập nhật thất bại");
          return;
        }
        toast.success("Cập nhật khoản chi thành công");
      } else {
        const res = await axiosInstance.post("/api/accounting/prepaid-expenses", payload);
        if (res?.data?.success === false) {
          toast.error(res.data.message || "Tạo thất bại");
          return;
        }
        toast.success("Tạo khoản chi thành công");
      }
      setShowModal(false);
      fetchData(page, appliedFilters);
    } catch (err) {
      toast.error(err.response?.data?.message || "Lưu thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (row, action, label) => {
    const result = await Swal.fire({
      title: `${label}?`,
      text: row.name ? `${row.code || ""} — ${row.name}` : undefined,
      icon: action === "cancel" ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: label,
      cancelButtonText: "Huỷ",
      confirmButtonColor: action === "cancel" ? "#dc3545" : "#0d6efd",
    });
    if (!result.isConfirmed) return;

    try {
      let res;
      if (action === "confirm") {
        const params = {};
        if (row.bankAccountId) params.bankAccountId = row.bankAccountId;
        res = await axiosInstance.put(
          `/api/accounting/prepaid-expenses/${row.id}/confirm`,
          null,
          { params }
        );
      } else {
        res = await axiosInstance.put(`/api/accounting/prepaid-expenses/${row.id}/${action}`);
      }
      if (res?.data?.success === false) {
        toast.error(res.data.message || `${label} thất bại`);
        return;
      }
      toast.success(`Đã ${label.toLowerCase()}`);
      fetchData(page, appliedFilters);
    } catch (err) {
      toast.error(err.response?.data?.message || `${label} thất bại`);
    }
  };


  const monthlyPreview = form.amount && form.depreciationMonths && Number(form.depreciationMonths) > 0
    ? Math.round(Number(form.amount) / Number(form.depreciationMonths))
    : null;

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      <Card className="mb-3 acc-filter-card">
        <Card.Body className="p-0">
          <AccountingFilterSection
            title="Chi phí trả trước / CCDC"
            open={filterOpen}
            onToggle={setFilterOpen}
            onSubmit={handleSearch}
            onReset={handleReset}
            activeCount={countActiveChips(activeChips)}
            toolbarRight={perm.canCreate && (
              <Button variant="primary" size="sm" onClick={openCreate}>
                <FaPlus className="me-1" /> Thêm khoản chi
              </Button>
            )}
          >
            <Row className="g-2 align-items-end acc-filter-grid">
              <Col>
                <Form.Label className="small mb-1">Từ khóa</Form.Label>
                <Form.Control
                  size="sm"
                  value={filterKeyword}
                  onChange={(e) => setFilterKeyword(e.target.value)}
                  placeholder="Mã / tên khoản chi"
                />
              </Col>
              <Col>
                <Form.Label className="small mb-1">Trạng thái</Form.Label>
                <Form.Select size="sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">Tất cả</option>
                  {Object.entries(STATUS_MAP).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </AccountingFilterSection>
          <AccountingActiveFilters
            chips={activeChips}
            onRemove={handleRemoveChip}
            onClearAll={handleReset}
          />
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5 text-muted">Chưa có khoản chi phí trả trước nào</div>
          ) : (
            <>
              <div className="acc-table-wrap">
                <Table striped hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Mã</th>
                      <th>Tên khoản chi</th>
                      <th className="acc-col-hide-sm">Đối tượng chi</th>
                      <th className="acc-col-hide-sm">Line</th>
                      <th className="text-end">Số tiền</th>
                      <th className="text-end acc-col-hide-sm">Đã phân bổ</th>
                      <th className="text-end acc-col-hide-sm">Còn lại</th>
                      <th className="text-center acc-col-hide-sm">KH (tháng)</th>
                      <th className="acc-col-hide-sm">Ngày chi</th>
                      <th className="text-center">Trạng thái</th>
                      <th className="acc-col-hide-sm">Phiếu chi</th>
                      <th className="text-center">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="fw-semibold">{r.code || `#${r.id}`}</td>
                        <td>{r.name}</td>
                        <td className="acc-col-hide-sm">{r.expenseObjectName || "—"}</td>
                        <td className="acc-col-hide-sm">
                          <div className="d-flex flex-wrap gap-1">
                            {(r.lineNames || []).length
                              ? r.lineNames.map((n, i) => (
                                <span key={`${r.id}-ln-${i}`} className="select3-item acc-line-tag">{n}</span>
                              ))
                              : <span className="text-muted">—</span>}
                          </div>
                        </td>
                        <td className="text-end fw-semibold">{fmtVND(r.amount)}</td>
                        <td className="text-end acc-col-hide-sm">{fmtVND(r.allocatedToDate)}</td>
                        <td className="text-end acc-col-hide-sm">{fmtVND(r.remainingAmount)}</td>
                        <td className="text-center acc-col-hide-sm">{r.depreciationMonths}</td>
                        <td className="acc-col-hide-sm">{r.expenseDate || "—"}</td>
                        <td className="text-center">
                          <Badge bg={STATUS_MAP[r.status]?.bg || "secondary"}>
                            {STATUS_MAP[r.status]?.label || r.status}
                          </Badge>
                        </td>
                        <td className="acc-col-hide-sm">
                          {r.paymentVoucherId ? (
                            <Link
                              to="/ke-toan/phieu-thu-chi?tab=payment"
                              className="text-decoration-none small"
                              title="Mở sổ phiếu chi"
                            >
                              <FaLink className="me-1" />
                              {r.paymentVoucherCode || `#${r.paymentVoucherId}`}
                            </Link>
                          ) : "—"}
                        </td>
                        <td className="text-center">
                          <div className="acc-action-btns">
                            {perm.canEdit && r.status === "DRAFT" && (
                              <>
                                <Button variant="outline-primary" size="sm" title="Sửa" onClick={() => openEdit(r)}>
                                  <FaEdit />
                                </Button>
                                <Button variant="outline-success" size="sm" title="Duyệt" onClick={() => handleAction(r, "approve", "Duyệt")}>
                                  <FaCheck />
                                </Button>
                                <Button variant="outline-danger" size="sm" title="Hủy" onClick={() => handleAction(r, "cancel", "Hủy")}>
                                  <FaTimes />
                                </Button>
                              </>
                            )}
                            {perm.canEdit && r.status === "APPROVED" && (
                              <>
                                <Button variant="outline-warning" size="sm" title="Xác nhận (tạo phiếu chi)" onClick={() => handleAction(r, "confirm", "Xác nhận")}>
                                  <FaMoneyBillWave />
                                </Button>
                                <Button variant="outline-secondary" size="sm" title="Mở lại" onClick={() => handleAction(r, "reopen", "Mở lại")}>
                                  <FaUndo />
                                </Button>
                                <Button variant="outline-danger" size="sm" title="Hủy" onClick={() => handleAction(r, "cancel", "Hủy")}>
                                  <FaTimes />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="acc-table-footer">
                <small className="text-muted">Tổng: {totalElements} bản ghi</small>
                <Pagination
                  hidePaginationWhenSinglePage
                  showItemsPerPage={false}
                  pageData={{ page: { number: page, totalPages, totalElements, size: PAGE_SIZE } }}
                  onPageChange={(p) => fetchData(p - 1)}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Sửa khoản chi" : "Thêm chi phí trả trước"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Tên khoản chi <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(e) => onFormChange("name", e.target.value)}
                    placeholder="VD: Mua máy in, mua công cụ..."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Đối tượng chi <span className="text-danger">*</span></Form.Label>
                  <Select
                    options={expenseObjectOptions}
                    value={selectedExpenseObject}
                    onChange={(opt) => onFormChange("expenseObjectId", opt ? opt.value : "")}
                    placeholder="Chọn đối tượng chi..."
                    isClearable
                    classNamePrefix="react-select"
                  />
                  {inheritedLines.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {inheritedLines.map((n, i) => (
                        <span key={`form-ln-${i}`} className="select3-item acc-line-tag">{n}</span>
                      ))}
                    </div>
                  )}
                  {selectedExpenseObject && inheritedLines.length === 0 && (
                    <Form.Text className="text-muted">Đối tượng chi chưa có Line kinh doanh</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Số tiền chi <span className="text-danger">*</span></Form.Label>
                  <MoneyInput
                    value={form.amount}
                    onValueChange={(v) => onFormChange("amount", v)}
                    placeholder="Nhập số tiền"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Thời gian khấu hao (tháng) <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="1"
                    value={form.depreciationMonths}
                    onChange={(e) => onFormChange("depreciationMonths", e.target.value)}
                    placeholder="VD: 12 (= 1 năm)"
                    required
                  />
                  {monthlyPreview != null && (
                    <Form.Text className="text-muted">
                      Phân bổ ~ {fmtVND(monthlyPreview)} / tháng
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Ngày chi</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.expenseDate}
                    onChange={(e) => onFormChange("expenseDate", e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Tài khoản ngân hàng</Form.Label>
                  <Form.Select
                    value={form.bankAccountId}
                    onChange={(e) => onFormChange("bankAccountId", e.target.value)}
                  >
                    <option value="">— Chọn khi xác nhận —</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {(b.bankName || "") + (b.accountNumber ? ` - ${b.accountNumber}` : "")}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Ghi chú</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.description}
                    onChange={(e) => onFormChange("description", e.target.value)}
                    placeholder="Mô tả thêm (tuỳ chọn)"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Đóng</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : (editingId ? "Lưu" : "Tạo mới")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default PrepaidExpenseList;
